import React, { useEffect } from "react";

import {
  GET_ORDER_COORDINATOR,
  DOCUMENTS_URL,
  INVOICES_URL,
  APPROVALS_URL,
  CARGOX_URL,
  CONDITIONS_URL,
  TRANSPORTATION_DOCUMENTS_URL,
} from "@API/APIRoutes";
import GenericOrderInfo from "../../GenericFullOrderInfo/GenericFullOrderInfo";
import useGetItem from "@Hooks/useGetItem";
import { useParams } from "react-router-dom";
import { useState } from "react";
import Box from "@mui/material/Box";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import themeCreator from "@Themes/themeCreator";
import { decryptId } from "../../../Utils/SecurityFunctions";
import { inputTypes } from "@Utils/HelpingObjects/HelpingObjects";

const orderInfoConfig = {
  tabs: [
    {
      label: "Primitive Info",
      subsections: [
        {
          title: "General Identification & Status",
          width: "500px",
          mr: 6,
          pr: 6,
          fields: [
            { label: "State", field: "state" },
            { label: "Origin", field: "origin" },
            { label: "PO Number", field: "poNumber" },
            { label: "Hscode", field: "hsCode" },
            { label: "Relative Company", field: "relativeCompany" },
            {
              label: "Category",
              field: "category.categoryName",
              fieldId: "categoryId",
            },
            {
              label: "Materials",
              field: "materials.materialName",
              fieldId: "materialId",
            },
            { label: "Agent", field: "agent.agentName", fieldId: "agentId" },
            {
              label: "Planner",
              field: "planner.plannerName",
              fieldId: "plannerId",
            },
            {
              label: "Supplier",
              field: "supplier.supplierName",
              fieldId: "supplierId",
            },
            { label: "Way Of Shipping", field: "wayOfShipping" },
            { label: "Is Sample", field: "isSample" },
          ],
        },
        {
          title: "General Identification (Date & Time)",
          width: "500px",
          mr: 6,
          pr: 6,
          fields: [
            {
              label: "PFI Receival Date",
              field: "pfiReceivalDate",
              formatter: "date",
            },
          ],
        },
      ],
      editable: false,
    },
    {
      label: "Order Info",
      subsections: [
        {
          title: "Order Details",
          width: "500px",
          mr: 6,
          pr: 6,
          fields: [
            { label: "Acid Number", field: "acidNumber" },
            { label: "Gross Weight", field: "grossWeight" },
            { label: "Net Weight", field: "netWeight" },
            { label: "Port Of Arrival", field: "portOfArrival" },
            {
              label: "Shipping Line",
              field: "shippingLine.shippingLineName",
              fieldId: "shippingLineId",
            },
            { label: "20' Containers", field: "container20" },
            { label: "40' Containers", field: "container40" },
            { label: "Pallets", field: "pallets" },
            { label: "Boxes", field: "boxes" },
            { label: "Booking Number ", field: "bookingNumber" },
            { label: "Shipping Port", field: "shippingPort" },
            { label: "Shipping Country", field: "shippingCountry" },
            { label: "Loadability", field: "loadability", capitalText: true },
            { label: "Drums", field: "drums" },
            { label: "General Comment", field: "generalComment" },
          ],
        },
        {
          title: "Order Info (Date & Time)",
          width: "500px",
          mr: 6,
          pr: 6,
          fields: [
            {
              label: "Acid Creation Date",
              field: "acidCreationDate",
              formatter: "date",
            },
            {
              label: "Shipped Onboard Date",
              field: "shippedOnboardDate",
              formatter: "date",
            },
          ],
        },
      ],
      editable: false,
    },
    {
      label: "Order Specific Info",
      subsections: [
        {
          title: "Order Specific Details",
          width: "500px",
          mr: 6,
          pr: 6,
          fields: [
            { label: "Order Number", field: "orderNumber" },
            { label: "Pending On", field: "pendingOn" },
            { label: "FOD", field: "fod" },
            { label: "Customs Certificate", field: "customCertificate" },
            {
              label: "Storage Location",
              field: "storageLocation.locationName",
              fieldId: "locationId",
            },
            {
              label: "Warehouse",
              field: "warehouse.warehouseName",
              fieldId: "warehouseId",
            },
            { label: "IBD Number", field: "ibdNumber" },
            { label: "Bank Linkage", field: "bankLinkage" },
            { label: "Non Relevant", field: "nonRelevant" },
          ],
        },
        {
          title: "Order Specific Details (Date & Time)",
          width: "500px",
          fields: [
            { label: "Release Date", field: "releaseDate", formatter: "date" },
            {
              label: "Expected Release Date",
              field: "expectedReleaseDate",
              formatter: "date",
            },
            {
              label: "Expected Arrival Date",
              field: "expectedArrivalDate",
              formatter: "date",
            },
            {
              label: "Actual Arrival Date",
              field: "actualArrivalDate",
              formatter: "date",
            },
            {
              label: "Customs Approval Date",
              field: "customsApprovalDate",
              formatter: "date",
            },
            {
              label: "Customs Release Date",
              field: "customsReleaseDate",
              formatter: "date",
            },
            {
              label: "Warehouse Arrival Date",
              field: "warehouseArrivalDate",
              formatter: "date",
            },
            { label: "IBD Date", field: "ibdDate", formatter: "date" },
            { label: "Storage Date", field: "storageDate", formatter: "date" },
          ],
        },
      ],
      editable: false,
    },
    {
      label: "Documents & CargoXs",
      subsections: [
        {
          title: "Documents",
          type: "Documents",
          width: "300px",
          mr: 3,
          pr: 1,
        },
      ],
      preview: {
        title: "Preview Document",
        width: "40vw",
        placeholder: "Select a document to preview",
      },
      editable: false,
    },
    {
      label: "Transportation",
      subsections: [
        {
          title: "Transportation",
          type: "Transportation",
          width: "100%",
          fields: [
            {
              label: "Trucks Numbers",
              field: "trucksNumbers",
              key: "trucksNumbers",
              type: inputTypes.TEXT,
            },
            {
              label: "Transportation Remarks",
              field: "transportationRemarks",
              key: "transportationRemarks",
              type: inputTypes.TEXT,
            },
            {
              label: "Time Of Loading",
              field: "timeOfLoading",
              key: "timeOfLoading",
              type: inputTypes.DATE,
              views: ["year", "month", "day", "hours", "minutes"],
              format: "DD/MM/YYYY HH:mm",
            },
            {
              label: "Warehouse Entry",
              field: "warehouseEntry",
              key: "warehouseEntry",
              type: inputTypes.DATE,
              views: ["year", "month", "day", "hours", "minutes"],
              format: "DD/MM/YYYY HH:mm",
            },
            {
              label: "Warehouse Exit",
              field: "warehouseExit",
              key: "warehouseExit",
              type: inputTypes.DATE,
              views: ["year", "month", "day", "hours", "minutes"],
              format: "DD/MM/YYYY HH:mm",
            },
          ],
        },
      ],
      editable: true,
    },
  ],
};

export default function TransportationCoordinatorFullOrderInfo() {
  const orderId = decryptId(useParams().orderId);
  const getItem = useGetItem();
  const [isLoading, setIsLoading] = useState(true);
  const [orderdata, setOrderData] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [cargoXs, setCargoXs] = useState(null);
  const [transportationDocuments, setTransportationDocuments] = useState(null);

  const theme = themeCreator();

  useEffect(() => {
    setIsLoading(true);

    const promises = [
      getItem(GET_ORDER_COORDINATOR, orderId),
      getItem(DOCUMENTS_URL, orderId),
      getItem(CARGOX_URL, orderId),
      getItem(TRANSPORTATION_DOCUMENTS_URL, orderId),
    ];

    Promise.all(promises)
      .then(([orderData, documents, cargoXs, transportationDocuments]) => {
        setOrderData(orderData);
        setDocuments(documents);
        setCargoXs(cargoXs);
        setTransportationDocuments(transportationDocuments);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, [orderId]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <GenericOrderInfo
      orderId={orderId}
      orderData={orderdata}
      config={orderInfoConfig}
      cargoXs={cargoXs}
      documents={documents}
      transportationDocuments={transportationDocuments}
    />
  );
}
